import React, { useEffect, useId, useRef } from 'react';
import { STATUSES } from '../shared/exchanges';

export function Icon({ name, size = 20 }) {
  const paths = {
    send: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 6 9 7 9-7" /></>,
    contact: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="M5 17c0-4 8-4 8 0M16 9h2M16 13h2" /></>,
    globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c5 5 5 13 0 18-5-5-5-13 0-18Z" /></>,
    plus: <path d="M12 5v14M5 12h14" />,
    download: <><path d="M12 3v12m-5-5 5 5 5-5M4 16v5h16v-5" /></>,
    search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></>,
    columns: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M9 4v16M15 4v16" /></>,
    filter: <path d="M3 4h18l-7 8v7l-4 2v-9Z" />,
    key: <><circle cx="8" cy="9" r="5" /><path d="m12 13 8 8m-3-3 3-3m-6 0 3-3" /></>,
    close: <path d="m6 6 12 12M6 18 18 6" />,
    arrow: <path d="M5 12h14m-6-6 6 6-6 6" />,
    check: <path d="m5 12 4 4L19 6" />,
    sort: <><path d="M7 4v16m-3-3 3 3 3-3M17 20V4m-3 3 3-3 3 3" /></>,
    folder: <path d="M3 7V5h6l2 2h10v13H3Z" />,
    logout: <><path d="M9 4H4v16h5M10 12h11m-4-4 4 4-4 4" /></>,
    grip: <><circle cx="9" cy="5" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="19" r="1" /></>
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name] || paths.folder}</svg>;
}

export function Notice({ children, type = 'error' }) {
  if (!children) return null;
  return <div className={`ex-notice ex-notice--${type}`} role={type === 'error' ? 'alert' : 'status'}>{children}</div>;
}

export function Modal({ embedded = false, ...props }) {
  if (embedded) return <section className="ex-record-panel" onKeyDown={event => {
    if (event.key === 'Escape' && !props.busy) { event.stopPropagation(); props.onClose(); }
  }}>
    <div className="ex-modal-heading"><h2>{props.title}</h2></div>
    {props.children}
  </section>;
  return <ModalDialog {...props} />;
}

function ModalDialog({ title, children, onClose, wide = false, busy = false }) {
  const dialog = useRef(null);
  const headingId = useId();
  const closeRef = useRef(onClose);
  const busyRef = useRef(busy);
  closeRef.current = onClose;
  busyRef.current = busy;
  useEffect(() => {
    const previous = document.activeElement;
    const element = dialog.current;
    const focusable = () => [...element.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]')].filter(node => node.getClientRects().length);
    (focusable()[0] || element).focus();
    const keydown = event => {
      if (event.key === 'Escape' && !busyRef.current) { event.preventDefault(); closeRef.current(); }
      if (event.key === 'Tab') {
        const nodes = focusable();
        if (!nodes.length) { event.preventDefault(); element.focus(); return; }
        if (event.shiftKey && (document.activeElement === nodes[0] || document.activeElement === element)) { event.preventDefault(); nodes[nodes.length - 1].focus(); }
        else if (!event.shiftKey && document.activeElement === nodes[nodes.length - 1]) { event.preventDefault(); nodes[0].focus(); }
      }
    };
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    element.addEventListener('keydown', keydown);
    return () => { document.body.style.overflow = oldOverflow; element.removeEventListener('keydown', keydown); previous?.focus(); };
  }, []);
  return <div className="ex-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <section ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={headingId} className={`ex-modal ${wide ? 'ex-modal--wide' : ''}`}>
      <div className="ex-modal-heading"><h2 id={headingId}>{title}</h2><button className="ex-icon-button" aria-label="Fermer" disabled={busy} onClick={onClose}><Icon name="close" /></button></div>
      {children}
    </section>
  </div>;
}

export function StatusBadge({ status, progress }) {
  return <span className={`ex-status ex-status--${status}`}><span className="ex-dot" />{STATUSES[status] || status}{status === 'in_progress' && <span className="ex-status-progress">{progress} %</span>}</span>;
}

export const formatDate = value => value ? new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeZone: 'Europe/Paris' }).format(new Date(Number(value))) : '—';
